<template>
  <div class="chat-input d-flex align-center pa-3">
    <!-- Estado normal: textarea + microfone + enviar -->
    <template v-if="!isRecording">
      <v-textarea
        ref="textarea"
        v-model="inputText"
        :placeholder="$t('clara.chatInput.placeholder')"
        rows="1"
        auto-grow
        hide-details
        dense
        outlined
        :disabled="disabled"
        class="chat-input__textarea"
        data-test="ai-chat-input"
        @keydown.enter.exact.prevent="handleSubmit"
      />

      <div
        v-if="isStreaming"
        class="ml-2"
      >
        <IconButtonWithTooltip
          icon="PhXCircle"
          :tooltip="$t('clara.chatInput.cancelResponse')"
          :size="22"
          :primary-color="false"
          data-test="ai-chat-stop"
          :action="() => $emit('stop')"
        />
      </div>

      <template v-else>
        <div
          v-if="isVoiceSupported"
          class="ml-2"
        >
          <IconButtonWithTooltip
            icon="PhMicrophone"
            :tooltip="$t('clara.chatInput.speakQuestion')"
            :size="22"
            data-test="ai-chat-microphone"
            :action="startRecording"
            :disabled="disabled"
          />
        </div>

        <div class="ml-2">
          <IconButtonWithTooltip
            icon="PhPaperPlaneTilt"
            :tooltip="$t('clara.chatInput.send')"
            :size="18"
            data-test="ai-chat-send"
            :action="handleSubmit"
            :disabled="isSubmitDisabled"
          />
        </div>
      </template>
    </template>

    <!-- Estado gravando: canvas de ondas + stop + send -->
    <template v-else>
      <div class="chat-input__wave-container flex-grow-1">
        <canvas
          ref="waveCanvas"
          class="chat-input__wave-canvas"
          height="36"
          data-test="ai-chat-wave-canvas"
          aria-hidden="true"
        ></canvas>
      </div>

      <div class="ml-2">
        <IconButtonWithTooltip
          icon="PhXCircle"
          :tooltip="$t('clara.chatInput.cancelRecording')"
          :size="20"
          :primary-color="false"
          data-test="ai-chat-voice-cancel"
          :action="cancelRecording"
        />
      </div>

      <div class="ml-2">
        <IconButtonWithTooltip
          icon="PhPaperPlaneTilt"
          :tooltip="$t('clara.chatInput.send')"
          :size="22"
          data-test="ai-chat-voice-send"
          :action="stopAndSend"
        />
      </div>
    </template>
  </div>
</template>

<script>
// Components
import IconButtonWithTooltip from '@/components/comum/IconButtonWithTooltip.vue';

export default {
  name: 'ChatInput',
  components: { IconButtonWithTooltip },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputText: '',
      isRecording: false,
      recognition: null,
      transcriptBuffer: '',
      audioContext: null,
      analyser: null,
      mediaStream: null,
      animationFrameId: null,
    };
  },
  computed: {
    isSubmitDisabled() {
      return this.disabled || !this.inputText.trim();
    },
    isVoiceSupported() {
      return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    },
  },
  watch: {
    /* O textarea fica desabilitado enquanto a resposta é gerada, e o browser
       manda o foco para o body quando isso acontece. Ao reabilitar, devolvemos
       o cursor para o campo — sem isso é preciso clicar para continuar. */
    disabled(isDisabled, wasDisabled) {
      if (wasDisabled && !isDisabled) this.focusInput();
    },
  },
  beforeDestroy() {
    this._cleanup();
  },
  methods: {
    focusInput() {
      if (this.isRecording || !this._focusIsIdle()) return;

      this.$nextTick(() => {
        if (this.$refs.textarea) this.$refs.textarea.focus();
      });
    },

    /* O painel da Clara empurra o app em vez de cobri-lo, então é comum o
       operador ir mexer em outra tela enquanto espera. Se ele já levou o foco
       para fora do painel, puxar o cursor de volta atropelaria o que estivesse
       digitando lá. Só reassumimos um foco que ninguém reivindicou. */
    _focusIsIdle() {
      const active = document.activeElement;

      if (!active || active === document.body || active === document.documentElement) return true;

      const panel = this.$el.closest ? this.$el.closest('.clara') : null;

      return panel ? panel.contains(active) : this.$el.contains(active);
    },

    handleSubmit() {
      if (this.isSubmitDisabled) return;

      this.$emit('send', this.inputText.trim());
      this.inputText = '';
    },

    async startRecording() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.lang = 'pt-BR';
      this.recognition.interimResults = true;
      this.recognition.continuous = true;
      this.transcriptBuffer = '';

      this.recognition.onresult = (event) => {
        this.transcriptBuffer = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
      };

      this.recognition.onerror = () => {
        this._cleanup();
        this.isRecording = false;
      };

      this.recognition.start();
      this.isRecording = true;

      await this._startWaveAnimation();
    },

    stopAndSend() {
      this._stopRecognition();

      if (this.transcriptBuffer.trim()) {
        this.inputText = this.transcriptBuffer.trim();
        this.$nextTick(() => this.handleSubmit());
      }

      this.isRecording = false;
      this.transcriptBuffer = '';
    },

    cancelRecording() {
      this._cleanup();
      this.isRecording = false;
      this.transcriptBuffer = '';
    },

    async _startWaveAnimation() {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;

        const source = this.audioContext.createMediaStreamSource(this.mediaStream);
        source.connect(this.analyser);

        this._drawWave();
      } catch (error) { // eslint-disable-next-line no-empty
      }
    },

    _drawWave() {
      const canvas = this.$refs.waveCanvas;

      if (!canvas || !this.analyser) return;

      const ctx = canvas.getContext('2d');
      const bufferLength = this.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        if (!this.isRecording) return;

        this.animationFrameId = requestAnimationFrame(draw);
        this.analyser.getByteFrequencyData(dataArray);

        const width = canvas.offsetWidth;
        canvas.width = width;
        const height = canvas.height;

        ctx.clearRect(0, 0, width, height);

        const barCount = 60;
        const barWidth = (width / barCount) * 0.35;
        const gap = (width / barCount) * 0.65;
        const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--v-primary-base') || '#1976d2';

        ctx.fillStyle = primaryColor;

        for (let i = 0; i < barCount; i++) {
          const dataIndex = Math.floor((i / barCount) * bufferLength);
          const amplitude = dataArray[dataIndex] / 255;
          const barHeight = Math.max(3, amplitude * height * 0.9);
          const x = i * (barWidth + gap);
          const y = (height - barHeight) / 2;

          ctx.beginPath();
          ctx.roundRect(x, y, barWidth, barHeight, 2);
          ctx.fill();
        }
      };

      draw();
    },

    _stopRecognition() {
      if (!this.recognition) return;

      this.recognition.onresult = null;
      this.recognition.onerror = null;
      this.recognition.stop();
      this.recognition = null;
    },

    _cleanup() {
      this._stopRecognition();

      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }

      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }

      this.analyser = null;
    },
  },
};
</script>

<style scoped>
.chat-input {
  border-top: 1px solid var(--tc-line);
  min-height: 64px;
}

.chat-input__textarea {
  max-height: 120px;
}

.chat-input__wave-container {
  display: flex;
  align-items: center;
  height: 40px;
  overflow: hidden;
  border-radius: var(--tc-radius-sm);
  border: 1px solid rgba(0, 0, 0, 0.38);
  padding: 0 12px;
  transition: border-color 0.15s ease;
}

.chat-input__wave-container:hover {
  border-color: rgba(0, 0, 0, 0.87);
}

.chat-input__wave-canvas {
  width: 100%;
  height: 36px;
  display: block;
}
</style>
